'use client';
import { Button } from '@curology/ui-components-web/react/Button';
import { Highlight } from '@curology/ui-components-web/react/Highlight';
import { ChatBubbleIcon } from '@curology/ui-components-web/react/Icon/icons/ChatBubbleIcon';
import { CheckmarkIcon } from '@curology/ui-components-web/react/Icon/icons/CheckmarkIcon';
import { GiftIcon } from '@curology/ui-components-web/react/Icon/icons/GiftIcon';
import { PostcardIcon } from '@curology/ui-components-web/react/Icon/icons/PostcardIcon';
import { Link } from '@curology/ui-components-web/react/Link';
import { Modal } from '@curology/ui-components-web/react/Modal';
import { ModalContent } from '@curology/ui-components-web/react/Modal/ModalContent';
import { ModalFooter } from '@curology/ui-components-web/react/Modal/ModalFooter';
import { TextInput } from '@curology/ui-components-web/react/TextInput';
import { Typography } from '@curology/ui-components-web/react/Typography';
import { useSetAtom } from 'jotai';
import Image from 'next/image';
import React, { useState } from 'react';

import { FlashMessagesState } from '@/atoms/flashMessages/flashMessagesState';
import { type Referral } from '@/types/referral';

const ShareCurologyWithAFriendClientContainer = ({
  referral,
}: {
  referral: Referral;
}) => {
  const setFlashMessages = useSetAtom(FlashMessagesState.flashMessagesAtom);

  const [showModal, setShowModal] = useState(true);

  return (
    <>
      <Highlight className="cur-text-primary">
        <Button
          buttonType="text"
          className="cur-border-0 cur-text-primary cur-underline"
          flush={true}
          onClick={() => setShowModal(true)}
        >
          Share Curology with a friend
        </Button>
        , and you&apos;ll both get $20* toward future shipments.
      </Highlight>
      <Modal
        onClose={() => setShowModal(false)}
        open={showModal}
        title=" "
        type="light"
      >
        <ModalContent className="cur-bg-surface-neutral cur-m-auto cur-max-w-[384px]">
          <Image
            alt="Share Curology with a Friend Modal Header Image"
            height={534}
            src="https://images.ctfassets.net/8pzh4g8cs30t/7npJrj7Fn1QjZOqdqn5cgB/50d39b49e292cc789ab08d086cc222da/share_curology_with_a_friend_modal_header_image.png"
            width={800}
          />
          <div className="cur-pl-4 cur-pr-4 cur-pt-4">
            <Typography className="cur-mb-4" size="h2">
              Give $20, get $20
            </Typography>
            <Typography className="cur-my-4">
              Invite a friend, and you&apos;ll both get $20 towards future boxes
              after they finish signing up.*
            </Typography>
            <Typography className="cur-my-4">
              The more friends that sign up with your link, the more credit you
              earn!
            </Typography>
            <div className="cur-my-4">
              <TextInput
                className="cur-text-primary"
                disabled={true}
                label="Share link"
                value={referral.link}
              />
            </div>
            <div className="cur-my-4">
              <Button
                className="cur-w-full"
                onClick={() => {
                  navigator.clipboard.writeText(referral.link);

                  setFlashMessages((flashMessages) => [
                    ...flashMessages,
                    {
                      icon: <CheckmarkIcon />,
                      iconSize: 'sm',
                      state: 'success',
                      title: 'Link copied!',
                    },
                  ]);
                }}
              >
                Copy link
              </Button>
            </div>
            <Typography className="cur-my-4 cur-text-center" size="meta">
              *Subject to medical consultation. Subscription required. Cancel
              anytime. A maximum of $20 in credits can be applied per order.{' '}
              <Link href="https://curology.com/rewardprogram/" target="_blank">
                See Terms
              </Link>
              .
            </Typography>
            <Typography className="cur-my-4" size="h2">
              How it works
            </Typography>
            <ul className="cur-mt-4 cur-list-none cur-p-0 cur-m-0">
              <li className="cur-flex cur-items-start cur-gap-4">
                <ChatBubbleIcon className="cur-flex-shrink-0 cur-w-6 cur-h-6" />
                <Typography>
                  Send your link to a friend who deserves some TLC
                </Typography>
              </li>
              <li className="cur-flex cur-items-start cur-gap-4 cur-mt-4">
                <PostcardIcon className="cur-flex-shrink-0 cur-w-6 cur-h-6" />
                <Typography>
                  They use your link to sign up for a Curology trial
                </Typography>
              </li>
              <li className="cur-flex cur-items-start cur-gap-4 cur-mt-4">
                <GiftIcon className="cur-flex-shrink-0 cur-w-6 cur-h-6" />
                <Typography>
                  We&apos;ll give you both $40* toward future boxes! Win-win
                </Typography>
              </li>
            </ul>
          </div>
        </ModalContent>
        <ModalFooter className="cur-bg-surface-neutral cur-m-auto cur-max-w-[384px] cur-p-4">
          <Button
            buttonType="secondary"
            className="cur-w-full"
            onClick={() => setShowModal(false)}
          >
            Done
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default ShareCurologyWithAFriendClientContainer;
